/**
 * Typed wrappers over `http.*` for every email-module backend route.
 *
 * The email module currently issues raw `fetch()` calls built on
 * `emailApiBase()` + the static-Bearer `getHeaders()` helper from
 * `@/lib/email-api`, which bypasses the http client's token rotation and 401
 * single-flight refresh-retry. This module re-expresses each of those routes as
 * a thin async function over `http.*` so the module can drop the raw network
 * layer in a later commit (this commit is purely ADDITIVE — no call sites are
 * swapped yet).
 *
 * Conventions (mirroring endpoints/drive-sync.ts + endpoints/channels.ts):
 *   - every fn takes an explicit `authToken?: string | null` threaded into the
 *     http options (so callers control token rotation / unauth requests),
 *   - request + response shapes are hand-typed: `xavior-openapi.yaml` has ZERO
 *     email paths, so there are no generated contract types for these routes —
 *     hand-typing is the established precedent (see endpoints/channels.ts),
 *   - each fn returns `response.data` (the parsed JSON body).
 *
 * The base path matches `emailApiPath()` from `@/lib/email-api`
 * (`/workspaces/{ws}/integrations/email`) so the routes are byte-identical to
 * the raw-fetch call sites these wrappers replace.
 *
 * NOTE: the streaming (SSE) variant of `/draft-reply` is intentionally NOT
 * wrapped here — it needs the raw `Response` stream. Only the plain-JSON
 * `/draft-reply` shape is exposed. The unrelated ChatBox card-action helper in
 * endpoints/email.ts is a different surface and is not touched.
 */
import { http } from '@/lib/http'
import { getActiveWorkspaceId } from '../workspace-context'

/** `/workspaces/{ws}/integrations/email` — mirrors emailApiPath(). */
function emailBasePath(): string {
  return `/workspaces/${getActiveWorkspaceId()}/integrations/email`
}

/* ------------------------------------------------------------------ *
 * Shared shapes
 * ------------------------------------------------------------------ */

/** A single recipient/sender address as the provider adapters emit it
 *  (`@xavior/integrations` `EmailAddress` = `{ name?; address }`). The DRAFT
 *  route (`email-proxy-drafts.router.ts`) spreads these verbatim, so a draft's
 *  `message.to` arrives as `EmailAddress[]`, NOT a string. Some legacy shapes
 *  carry `email` instead of `address`; both are accepted on read. Web may not
 *  import `@xavior/integrations`, so this mirror lives here as the single
 *  honest definition the email module imports. */
export interface EmailAddress {
  name?: string
  address?: string
  email?: string
}

/** A recipient field (`to`/draft `to`) as it actually reaches the web client:
 *  a comma-joined string (the THREAD route flattens array→string) OR an
 *  `EmailAddress[]` (the DRAFT route spreads provider objects verbatim). That
 *  asymmetry is the string-vs-object trap — always normalize through
 *  `toRecipientList` / `firstRecipient` (modules/email/utils/senderParsing.ts)
 *  before any scalar (`.split`/`.match`/`.includes`) op. */
export type RecipientFieldValue = string | EmailAddress[]

/** An account row as returned by `/status`. */
export interface EmailAccountSummary {
  id: string
  email: string
  provider?: string
  tokenValid?: boolean
  enabledForModule?: boolean
  status?: 'active' | 'disabled'
  /** Primary (write) account for the module — only set when `/status` is called with a moduleId. */
  isPrimary?: boolean
}

export interface EmailStatusResponse {
  connected: boolean
  account?: unknown
  accounts?: EmailAccountSummary[]
}

/** A cached-inbox / folder message row. Only the consumed fields are typed. */
export interface EmailMessageRow {
  id: string
  integrationId?: string
  threadId?: string
  subject?: string
  fromEmail?: string
  fromName?: string
  fromRaw?: string
  date?: string
  snippet?: string
  labelIds?: string[]
  isUnread?: boolean
  status?: string
  category?: string
  [key: string]: unknown
}

export interface EmailMessagesResponse {
  messages?: EmailMessageRow[]
}

export interface EmailDraftRow {
  id: string
  integrationId?: string
  message?: {
    /** The proxy returns parsed mailbox objects (`[{ address }]`, sometimes
     *  `{ email }`) for provider drafts (email-proxy-drafts.router.ts) — this
     *  honest array-of-object type forces consumers to normalize rather than
     *  string-op a value that was never really a string (F1/F2/F6/F7). */
    to?: RecipientFieldValue
    subject?: string
    threadId?: string
    date?: string
    bodyHtml?: string
    bodyText?: string
  }
  [key: string]: unknown
}

/* ------------------------------------------------------------------ *
 * Status / search
 * ------------------------------------------------------------------ */

/** `GET /status` (optionally scoped to a module instance). */
export async function getEmailStatus(
  params?: { moduleId?: string },
  authToken?: string | null,
): Promise<EmailStatusResponse> {
  const path = params?.moduleId
    ? `${emailBasePath()}/status?moduleId=${encodeURIComponent(params.moduleId)}`
    : `${emailBasePath()}/status`
  const res = await http.get<EmailStatusResponse>(path, { authToken })
  return res.data
}

export interface EmailSearchResponse {
  resultSizeEstimate?: number
  threads?: unknown[]
}

/** `POST /search` — used for the unread-count probe. */
export async function searchEmail(
  body: { query: string; max?: number; accountId?: string },
  authToken?: string | null,
): Promise<EmailSearchResponse> {
  const res = await http.post<EmailSearchResponse>(`${emailBasePath()}/search`, body, { authToken })
  return res.data
}

/* ------------------------------------------------------------------ *
 * Cached inbox / folders / counts
 * ------------------------------------------------------------------ */

export interface CachedInboxParams {
  limit: number
  offset: number
  grouped?: boolean
  category?: string
  priorityLevel?: number
  fromEmail?: string
  /** Pre-joined, comma-separated, already-encoded account id list. */
  accountIds?: string
}

/**
 * `GET /cached-inbox` — the paginated/grouped server-cached inbox.
 *
 * `accountIds` is passed through verbatim (callers pre-join + pre-encode the
 * id list exactly as the raw call sites did), so the produced query string is
 * byte-identical to the existing `buildCachedUrl` output.
 */
export async function getCachedInbox(
  params: CachedInboxParams,
  authToken?: string | null,
): Promise<EmailMessagesResponse> {
  const qs = new URLSearchParams()
  qs.set('limit', String(params.limit))
  qs.set('offset', String(params.offset))
  if (params.grouped !== undefined) qs.set('grouped', String(params.grouped))
  if (params.category !== undefined) qs.set('category', params.category)
  if (params.priorityLevel !== undefined) qs.set('priorityLevel', String(params.priorityLevel))
  if (params.fromEmail !== undefined) qs.set('fromEmail', params.fromEmail)
  if (params.accountIds !== undefined) qs.set('accountIds', params.accountIds)
  const res = await http.get<EmailMessagesResponse>(
    `${emailBasePath()}/cached-inbox?${qs.toString()}`,
    { authToken },
  )
  return res.data
}

/** `GET /folder/{folder}` — server-side folder listing (sent/trash/etc.). */
export async function getFolderMessages(
  folder: string,
  params: { max?: number; accountIds?: string },
  authToken?: string | null,
): Promise<EmailMessagesResponse> {
  const qs = new URLSearchParams()
  if (params.max !== undefined) qs.set('max', String(params.max))
  if (params.accountIds !== undefined) qs.set('accountIds', params.accountIds)
  const query = qs.toString()
  const res = await http.get<EmailMessagesResponse>(
    `${emailBasePath()}/folder/${folder}${query ? `?${query}` : ''}`,
    { authToken },
  )
  return res.data
}

export interface CategoryCountsResponse {
  counts?: Record<string, number> | null
}

/** `GET /categories` — tab/Priority badge counts. */
export async function getCategoryCounts(
  params: { accountIds?: string },
  authToken?: string | null,
): Promise<CategoryCountsResponse> {
  const path = params.accountIds !== undefined
    ? `${emailBasePath()}/categories?accountIds=${params.accountIds}`
    : `${emailBasePath()}/categories`
  const res = await http.get<CategoryCountsResponse>(path, { authToken })
  return res.data
}

/* ------------------------------------------------------------------ *
 * Contacts / priority senders
 * ------------------------------------------------------------------ */

export interface PrioritySender {
  fromEmail: string
  fromName?: string
  senderPhotoUrl?: string
  totalCount: number
  unreadCount: number
  latestDate?: string
}

export interface PrioritySendersResponse {
  senders?: PrioritySender[]
}

/** `GET /priority-senders` — the Senders view list. */
export async function getPrioritySenders(
  params: { limit?: number; scope?: string; accountIds?: string },
  authToken?: string | null,
): Promise<PrioritySendersResponse> {
  let path = `${emailBasePath()}/priority-senders?limit=${params.limit ?? 200}&scope=${params.scope ?? ''}`
  if (params.accountIds) path += `&accountIds=${params.accountIds}`
  const res = await http.get<PrioritySendersResponse>(path, { authToken })
  return res.data
}

/** `POST /senders/{fromEmail}/bulk-action` — bulk read/ignore/demote. */
export async function senderBulkAction(
  fromEmail: string,
  body: { action: 'read' | 'ignore' | 'demote'; accountIds?: string },
  authToken?: string | null,
): Promise<unknown> {
  const res = await http.post<unknown>(
    `${emailBasePath()}/senders/${encodeURIComponent(fromEmail)}/bulk-action`,
    body,
    { authToken },
  )
  return res.data
}

/* ------------------------------------------------------------------ *
 * Drafts
 * ------------------------------------------------------------------ */

export interface DraftsListResponse {
  drafts?: EmailDraftRow[]
}

/** `GET /drafts` — the drafts-view list. */
export async function listDrafts(
  params: { max?: number; accountIds?: string },
  authToken?: string | null,
): Promise<DraftsListResponse> {
  let path = `${emailBasePath()}/drafts?max=${params.max ?? 50}`
  if (params.accountIds !== undefined) path += `&accountIds=${params.accountIds}`
  const res = await http.get<DraftsListResponse>(path, { authToken })
  return res.data
}

export interface DraftDetailResponse {
  draft?: EmailDraftRow
}

/** `GET /drafts/{draftId}` — full draft content. */
export async function getDraft(
  draftId: string,
  params: { accountId?: string },
  authToken?: string | null,
): Promise<DraftDetailResponse> {
  const query = params.accountId ? `?accountId=${encodeURIComponent(params.accountId)}` : ''
  const res = await http.get<DraftDetailResponse>(
    `${emailBasePath()}/drafts/${encodeURIComponent(draftId)}${query}`,
    { authToken },
  )
  return res.data
}

export interface DraftSaveBody {
  to: string
  subject: string
  body: string
  threadId?: string
  accountId?: string
}

/** `POST /drafts` — create a new draft. */
export async function createDraft(
  body: DraftSaveBody,
  authToken?: string | null,
): Promise<DraftDetailResponse> {
  const res = await http.post<DraftDetailResponse>(`${emailBasePath()}/drafts`, body, { authToken })
  return res.data
}

/** `PUT /drafts/{draftId}` — update an existing draft in place. */
export async function updateDraft(
  draftId: string,
  body: DraftSaveBody,
  authToken?: string | null,
): Promise<DraftDetailResponse> {
  const res = await http.put<DraftDetailResponse>(
    `${emailBasePath()}/drafts/${encodeURIComponent(draftId)}`,
    body,
    { authToken },
  )
  return res.data
}

/** `DELETE /drafts/{draftId}` — delete a draft (optionally account-scoped). */
export async function deleteDraft(
  draftId: string,
  params: { accountId?: string },
  authToken?: string | null,
): Promise<void> {
  const query = params.accountId ? `?accountId=${encodeURIComponent(params.accountId)}` : ''
  await http.delete(`${emailBasePath()}/drafts/${encodeURIComponent(draftId)}${query}`, { authToken })
}

/** `POST /drafts/{draftId}/send` — send a saved draft. */
export async function sendDraft(
  draftId: string,
  params: { accountId?: string },
  authToken?: string | null,
): Promise<unknown> {
  const query = params.accountId ? `?accountId=${encodeURIComponent(params.accountId)}` : ''
  const res = await http.post<unknown>(
    `${emailBasePath()}/drafts/${encodeURIComponent(draftId)}/send${query}`,
    {},
    { authToken },
  )
  return res.data
}

/* ------------------------------------------------------------------ *
 * Threads / compose
 * ------------------------------------------------------------------ */

export interface ThreadResponse {
  id?: string
  threadId?: string
  subject?: string
  degraded?: boolean
  reconnectRequired?: boolean
  email?: string
  messages?: Array<Record<string, unknown>>
}

/** `POST /thread` — load a full thread. */
export async function getThread(
  body: { threadId: string; accountId?: string },
  authToken?: string | null,
): Promise<ThreadResponse> {
  const res = await http.post<ThreadResponse>(`${emailBasePath()}/thread`, body, { authToken })
  return res.data
}

export interface DraftReplyBody {
  threadId: string
  subject: string
  messages: Array<{ from: string; body: string }>
  draftMode?: string
  accountId?: string
}

export interface DraftReplyResponse {
  draft?: string
  text?: string
  [key: string]: unknown
}

/**
 * `POST /draft-reply` — the plain-JSON (non-streaming) AI draft variant.
 *
 * The SSE streaming variant of this route is deliberately NOT wrapped here; it
 * needs raw stream access and stays on its own raw-fetch path.
 */
export async function generateDraftReply(
  body: DraftReplyBody,
  authToken?: string | null,
): Promise<DraftReplyResponse> {
  const res = await http.post<DraftReplyResponse>(`${emailBasePath()}/draft-reply`, body, { authToken })
  return res.data
}

export interface SendEmailBody {
  to: string
  subject: string
  body: string
  cc?: string
  bcc?: string
  threadId?: string
  messageId?: string
  moduleId?: string
  accountId?: string
  draftMode?: string
  attachments?: Array<{ filename: string; mimeType: string; data: string }>
}

/** `POST /send` — send a new message or thread reply. */
export async function sendEmail(
  body: SendEmailBody,
  authToken?: string | null,
): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/send`, body, { authToken })
  return res.data
}

/* ------------------------------------------------------------------ *
 * Per-message mutations
 * ------------------------------------------------------------------ */

export interface MessageActionBody {
  messageId: string
  accountId?: string
}

/** `POST /mark-read`. */
export async function markRead(body: MessageActionBody, authToken?: string | null): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/mark-read`, body, { authToken })
  return res.data
}

/** `POST /mark-unread`. */
export async function markUnread(body: MessageActionBody, authToken?: string | null): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/mark-unread`, body, { authToken })
  return res.data
}

/** `POST /toggle-important`. */
export async function toggleImportant(
  body: { messageId: string; important: boolean; accountId?: string },
  authToken?: string | null,
): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/toggle-important`, body, { authToken })
  return res.data
}

/** `POST /archive`. */
export async function archiveMessage(body: MessageActionBody, authToken?: string | null): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/archive`, body, { authToken })
  return res.data
}

/** `POST /unarchive`. */
export async function unarchiveMessage(body: MessageActionBody, authToken?: string | null): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/unarchive`, body, { authToken })
  return res.data
}

/** `POST /untrash`. */
export async function untrashMessage(body: MessageActionBody, authToken?: string | null): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/untrash`, body, { authToken })
  return res.data
}

/** `POST /trash`. */
export async function trashMessage(body: MessageActionBody, authToken?: string | null): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/trash`, body, { authToken })
  return res.data
}

/** `POST /report-spam`. */
export async function reportSpam(body: MessageActionBody, authToken?: string | null): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/report-spam`, body, { authToken })
  return res.data
}

/** `POST /snooze`. */
export async function snoozeMessage(
  body: { messageId: string; snoozeUntil: string; accountId?: string },
  authToken?: string | null,
): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/snooze`, body, { authToken })
  return res.data
}

/** `POST /set-status`. */
export async function setMessageStatus(
  body: { messageId: string; status: string; accountId?: string },
  authToken?: string | null,
): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/set-status`, body, { authToken })
  return res.data
}

/** `POST /reclassify-sender`. */
export async function reclassifySender(
  body: { senderKey: string; category: string },
  authToken?: string | null,
): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/reclassify-sender`, body, { authToken })
  return res.data
}

/** `POST /unsubscribe` — best-effort list-unsubscribe (optionally account-scoped). */
export async function unsubscribe(
  body: { messageId: string },
  params: { accountId?: string },
  authToken?: string | null,
): Promise<unknown> {
  const query = params.accountId ? `?accountId=${encodeURIComponent(params.accountId)}` : ''
  const res = await http.post<unknown>(`${emailBasePath()}/unsubscribe${query}`, body, { authToken })
  return res.data
}

/* ------------------------------------------------------------------ *
 * Bulk mutations
 * ------------------------------------------------------------------ */

export interface BulkMessagesBody {
  messageIds: string[]
  accountId?: string
}

/** `POST /bulk-mark-read`. */
export async function bulkMarkRead(body: BulkMessagesBody, authToken?: string | null): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/bulk-mark-read`, body, { authToken })
  return res.data
}

/** `POST /bulk-archive`. */
export async function bulkArchive(body: BulkMessagesBody, authToken?: string | null): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/bulk-archive`, body, { authToken })
  return res.data
}

/** `POST /bulk-trash`. */
export async function bulkTrash(body: BulkMessagesBody, authToken?: string | null): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/bulk-trash`, body, { authToken })
  return res.data
}

/** `POST /bulk-report-spam`. */
export async function bulkReportSpam(body: BulkMessagesBody, authToken?: string | null): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/bulk-report-spam`, body, { authToken })
  return res.data
}

/** `POST /bulk-set-status`. */
export async function bulkSetStatus(
  body: { messageIds: string[]; status: string; accountId?: string },
  authToken?: string | null,
): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/bulk-set-status`, body, { authToken })
  return res.data
}

/* ------------------------------------------------------------------ *
 * Sync
 * ------------------------------------------------------------------ */

/** `POST /sync-now` — kick a backend sync (fire-and-forget at the call sites). */
export async function syncNow(authToken?: string | null): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/sync-now`, {}, { authToken })
  return res.data
}

export interface SyncStatusResponse {
  messageCount?: number
  status?: string
}

/** `GET /sync-status` — sync progress poll. */
export async function getSyncStatus(authToken?: string | null): Promise<SyncStatusResponse> {
  const res = await http.get<SyncStatusResponse>(`${emailBasePath()}/sync-status`, { authToken })
  return res.data
}

/* ------------------------------------------------------------------ *
 * Module account enable / disable
 * ------------------------------------------------------------------ */

/** `POST /{accountId}/modules/{moduleId}/enable` — enable an account for a module. */
export async function enableAccountForModule(
  accountId: string,
  moduleId: string,
  authToken?: string | null,
): Promise<unknown> {
  const res = await http.post<unknown>(
    `${emailBasePath()}/${accountId}/modules/${moduleId}/enable`,
    {},
    { authToken },
  )
  return res.data
}

/** `DELETE /{accountId}/modules/{moduleId}/disable` — disable an account for a module. */
export async function disableAccountForModule(
  accountId: string,
  moduleId: string,
  authToken?: string | null,
): Promise<void> {
  await http.delete(`${emailBasePath()}/${accountId}/modules/${moduleId}/disable`, { authToken })
}

/* ------------------------------------------------------------------ *
 * Intelligence (used by modules/email/intelligence/hooks.ts)
 * ------------------------------------------------------------------ */

/** `GET /intelligence` — the email intelligence summary. */
export async function getIntelligence<T = unknown>(authToken?: string | null): Promise<T> {
  const res = await http.get<T>(`${emailBasePath()}/intelligence`, { authToken })
  return res.data
}

/** `GET /intelligence/coverage` — intelligence coverage stats. */
export async function getIntelligenceCoverage<T = unknown>(authToken?: string | null): Promise<T> {
  const res = await http.get<T>(`${emailBasePath()}/intelligence/coverage`, { authToken })
  return res.data
}

/* ------------------------------------------------------------------ *
 * Writing styles / stats
 * ------------------------------------------------------------------ */

export interface WritingStylesResponse {
  cascade?: string
  hasPerContactProfile?: boolean
  contactProfileSentCount?: number
  hasAggregateProfile?: boolean
  aggregateSentCount?: number
  [key: string]: unknown
}

/** `GET /writing-styles?contactEmail=…` — the AI-draft writing-profile status. */
export async function getWritingStyles<T = WritingStylesResponse>(
  params: { contactEmail: string },
  authToken?: string | null,
): Promise<T> {
  const res = await http.get<T>(
    `${emailBasePath()}/writing-styles?contactEmail=${encodeURIComponent(params.contactEmail)}`,
    { authToken },
  )
  return res.data
}

/**
 * `GET /stats` — the Email 360 stats report.
 *
 * `accountIds` is passed through verbatim (callers pre-join + pre-encode the id
 * list exactly as the raw call site did), so the produced query string is
 * byte-identical to the existing `loadStats` URL.
 */
export async function getEmailStats<T = unknown>(
  params: { accountIds?: string },
  authToken?: string | null,
): Promise<T> {
  const path = params.accountIds
    ? `${emailBasePath()}/stats?accountIds=${params.accountIds}`
    : `${emailBasePath()}/stats`
  const res = await http.get<T>(path, { authToken })
  return res.data
}

/* ------------------------------------------------------------------ *
 * CRM contacts (sender → contact linking)
 * ------------------------------------------------------------------ */

export interface ContactsListResponse {
  items?: Array<Record<string, unknown>>
  [key: string]: unknown
}

/**
 * `GET /contacts?limit=…` — the CRM contact list the email module uses to link
 * senders to existing contacts (sender chips, dedupe on add).
 *
 * NOTE: this is the platform `/contacts` route (NOT workspace-scoped under the
 * email base path) — it deliberately resolves against `env.apiBaseUrl` directly,
 * byte-identical to the raw `${env.apiBaseUrl}/contacts?limit=500` call site.
 */
export async function listContacts<T = ContactsListResponse>(
  params: { limit: number },
  authToken?: string | null,
): Promise<T> {
  const res = await http.get<T>(`/contacts?limit=${params.limit}`, { authToken })
  return res.data
}

/**
 * `GET /workspaces/{ws}/contacts?query=…` — the workspace-scoped contact search
 * AnalysisView uses to resolve a profile-mentioned name to an existing contact.
 *
 * This is the workspace `/contacts` route (NOT the email base path, NOT the
 * platform global `/contacts` used by `listContacts`). `query` is encoded; the
 * trailing `limit=20&includeArchived=true` is fixed, byte-identical to the raw
 * AnalysisView call site.
 */
export async function searchWorkspaceContacts<T = ContactsListResponse>(
  params: { query: string; limit?: number; includeArchived?: boolean },
  authToken?: string | null,
): Promise<T> {
  const limit = params.limit ?? 20
  const includeArchived = params.includeArchived ?? true
  const path =
    `/workspaces/${getActiveWorkspaceId()}/contacts` +
    `?query=${encodeURIComponent(params.query)}&limit=${limit}&includeArchived=${includeArchived}`
  const res = await http.get<T>(path, { authToken })
  return res.data
}

/**
 * `POST /workspaces/{ws}/contacts` — create a workspace contact (used by
 * AnalysisView when a profile-mentioned name has no existing contact).
 *
 * A fresh `Idempotency-Key` is attached per call (matching the raw call site,
 * which minted one with `crypto.randomUUID()`).
 */
export async function createWorkspaceContact<T = { id?: string } & Record<string, unknown>>(
  body: { name: string; title: string },
  authToken?: string | null,
): Promise<T> {
  const res = await http.post<T>(
    `/workspaces/${getActiveWorkspaceId()}/contacts`,
    body,
    { authToken, headers: { 'Idempotency-Key': crypto.randomUUID() } },
  )
  return res.data
}

/* ------------------------------------------------------------------ *
 * Email 360 profile / analysis (used by AnalysisView)
 * ------------------------------------------------------------------ */

export interface UserFactRow {
  id: string
  text: string
  createdAt: string
}

export interface UserFactsResponse {
  facts?: UserFactRow[]
}

/** `GET /user-facts` — the "what xavior should know" correction list. */
export async function getUserFacts<T = UserFactsResponse>(authToken?: string | null): Promise<T> {
  const res = await http.get<T>(`${emailBasePath()}/user-facts`, { authToken })
  return res.data
}

/** `POST /user-facts` — add a correction. */
export async function addUserFact(
  body: { text: string },
  authToken?: string | null,
): Promise<unknown> {
  const res = await http.post<unknown>(`${emailBasePath()}/user-facts`, body, { authToken })
  return res.data
}

/** `DELETE /user-facts/{factId}` — remove a correction. */
export async function deleteUserFact(factId: string, authToken?: string | null): Promise<void> {
  await http.delete(`${emailBasePath()}/user-facts/${encodeURIComponent(factId)}`, { authToken })
}

export interface AnalysisReportRow {
  id: string
  reportNumber: number
  text: string
  messageCount: number | null
  model: string | null
  comment: string | null
  createdAt: string
}

export interface AnalysisReportsResponse {
  reports?: AnalysisReportRow[]
}

/** `GET /analysis-reports` — the profile report history. */
export async function getAnalysisReports<T = AnalysisReportsResponse>(
  authToken?: string | null,
): Promise<T> {
  const res = await http.get<T>(`${emailBasePath()}/analysis-reports`, { authToken })
  return res.data
}

/** `PATCH /analysis-reports/{reportId}` — edit a report's comment. */
export async function updateAnalysisReport(
  reportId: string,
  body: { comment: string | null },
  authToken?: string | null,
): Promise<unknown> {
  const res = await http.patch<unknown>(
    `${emailBasePath()}/analysis-reports/${encodeURIComponent(reportId)}`,
    body,
    { authToken },
  )
  return res.data
}

/** `DELETE /analysis-reports/{reportId}` — delete a report. */
export async function deleteAnalysisReport(reportId: string, authToken?: string | null): Promise<void> {
  await http.delete(`${emailBasePath()}/analysis-reports/${encodeURIComponent(reportId)}`, { authToken })
}

/**
 * `POST /analysis` — (re)generate the Email 360 profile.
 *
 * `accountIds` is passed through verbatim (the caller pre-joins + pre-encodes the
 * id list exactly as the raw call site did), so the produced query string is
 * byte-identical to the existing AnalysisView `generate` URL. Body is `{}`.
 */
export async function generateAnalysis<T = unknown>(
  params: { accountIds?: string },
  authToken?: string | null,
): Promise<T> {
  const path = params.accountIds
    ? `${emailBasePath()}/analysis?accountIds=${params.accountIds}`
    : `${emailBasePath()}/analysis`
  const res = await http.post<T>(path, {}, { authToken })
  return res.data
}
